import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { DataTable } from '@/components/ui/data-table'
import { Modal } from '@/components/ui/modal'
import { StatusBadge } from '@/components/ui/status-badge'
import { useUpdateAlertRule } from '@/hooks/use-alerts'
import type { AlertRule } from '@/lib/types'

const OPERATOR_LABELS: Record<string, string> = {
  gt: '>',
  lt: '<',
  eq: '=',
}

interface AlertRulesTableProps {
  rules: AlertRule[]
  isLoading: boolean
}

export function AlertRulesTable({ rules, isLoading }: AlertRulesTableProps): React.JSX.Element {
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null)

  const columns = [
    {
      key: 'metric',
      label: 'Metric',
      render: (row: AlertRule) => (
        <span className="font-medium">{row.metric.replace(/_/g, ' ')}</span>
      ),
    },
    {
      key: 'condition',
      label: 'Condition',
      render: (row: AlertRule) =>
        `${OPERATOR_LABELS[row.operator] ?? row.operator} ${row.threshold.toLocaleString()}`,
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (row: AlertRule) => <StatusBadge status={row.severity} />,
    },
    {
      key: 'enabled',
      label: 'Enabled',
      render: (row: AlertRule) => (
        <span className={row.enabled ? 'text-green-600' : 'text-text-tertiary'}>
          {row.enabled ? 'On' : 'Off'}
        </span>
      ),
    },
    {
      key: 'notify_email',
      label: 'Email',
      render: (row: AlertRule) => (row.notify_email ? 'Yes' : 'No'),
    },
    {
      key: 'notify_slack',
      label: 'Slack',
      render: (row: AlertRule) => (row.notify_slack ? 'Yes' : 'No'),
    },
    {
      key: 'cooldown',
      label: 'Cooldown',
      render: (row: AlertRule) => `${row.cooldown_minutes}m`,
    },
    {
      key: 'last_fired',
      label: 'Last Fired',
      render: (row: AlertRule) =>
        row.last_fired_at ? new Date(row.last_fired_at).toLocaleString() : 'Never',
    },
    {
      key: 'actions',
      label: '',
      render: (row: AlertRule) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setEditingRule(row)
          }}
          className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 hover:text-text-primary"
        >
          <Pencil className="h-4 w-4" />
        </button>
      ),
    },
  ]

  return (
    <>
      <DataTable<AlertRule>
        columns={columns}
        data={rules}
        isLoading={isLoading}
        emptyMessage="No alert rules configured"
      />
      {editingRule && <EditRuleModal rule={editingRule} onClose={() => setEditingRule(null)} />}
    </>
  )
}

interface EditRuleModalProps {
  rule: AlertRule
  onClose: () => void
}

function EditRuleModal({ rule, onClose }: EditRuleModalProps): React.JSX.Element {
  const updateRule = useUpdateAlertRule()
  const [threshold, setThreshold] = useState(rule.threshold)
  const [severity, setSeverity] = useState(rule.severity)
  const [enabled, setEnabled] = useState(rule.enabled)
  const [notifyEmail, setNotifyEmail] = useState(rule.notify_email)
  const [notifySlack, setNotifySlack] = useState(rule.notify_slack)
  const [cooldown, setCooldown] = useState(rule.cooldown_minutes)

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    updateRule.mutate(
      {
        id: rule.id,
        threshold,
        severity,
        enabled,
        notify_email: notifyEmail,
        notify_slack: notifySlack,
        cooldown_minutes: cooldown,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal open onClose={onClose} title={`Edit: ${rule.metric.replace(/_/g, ' ')}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="alert-threshold" className="block text-sm font-medium text-text-primary">
            Threshold
          </label>
          <input
            id="alert-threshold"
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary"
          />
        </div>
        <div>
          <label htmlFor="alert-severity" className="block text-sm font-medium text-text-primary">
            Severity
          </label>
          <select
            id="alert-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as 'warning' | 'critical')}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary"
          >
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded"
            />
            Enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
              className="rounded"
            />
            Email
          </label>
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={notifySlack}
              onChange={(e) => setNotifySlack(e.target.checked)}
              className="rounded"
            />
            Slack
          </label>
        </div>
        <div>
          <label htmlFor="alert-cooldown" className="block text-sm font-medium text-text-primary">
            Cooldown (minutes)
          </label>
          <input
            id="alert-cooldown"
            type="number"
            value={cooldown}
            onChange={(e) => setCooldown(Number(e.target.value))}
            min={1}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateRule.isPending}
            className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
          >
            {updateRule.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
